/**
 * Datos de Sedes (T071, implementation-notes §4): TanStack Query sobre `api/client.ts`.
 * Clave de query `['sites']` (con los filtros como sufijo para cachear cada combinación;
 * invalidar `['sites']` refresca todas). Cada respuesta se valida con el esquema del contrato.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  SiteListResponseSchema,
  SiteSchema,
  type CreateSiteBody,
  type Site,
  type SiteListQuery,
  type UpdateSiteBody,
} from '@fermin/contracts';

import { api, type ApiError } from '../../api/client';

export const SITES_QUERY_KEY = ['sites'] as const;

/** `GET /sites?status&q` — el alcance por sede lo aplica la API, nunca el cliente. */
export function sitesPath(filters: SiteListQuery = {}): string {
  const params = new URLSearchParams();
  if (filters.status) {
    params.set('status', filters.status);
  }
  if (filters.q) {
    params.set('q', filters.q);
  }
  const query = params.toString();
  return query ? `/sites?${query}` : '/sites';
}

export function fetchSites(filters: SiteListQuery = {}): Promise<Site[]> {
  return api.get(sitesPath(filters), SiteListResponseSchema).then((page) => page.items);
}

export interface UseSitesOptions {
  /**
   * Fuerza un refetch al montar aunque el dato en caché siga "fresco" (`staleTime`). Úsalo en
   * pantallas que deciden un estado vacío/bloqueante a partir de esta lista (p. ej. "primero
   * necesitas una sede activa"): otra pantalla pudo poblar la misma clave con una lista vieja
   * (caso real: el selector de sede del dashboard consulta `['sites', {status:'ACTIVE'}]`, la
   * misma clave que usan Marcadores/Trabajadores) y una sede creada después no debe leerse como
   * "no hay sedes".
   */
  refetchOnMount?: boolean | 'always';
}

export function useSites(
  filters: SiteListQuery = {},
  options: UseSitesOptions = {},
): UseQueryResult<Site[], ApiError> {
  return useQuery<Site[], ApiError>({
    queryKey: [...SITES_QUERY_KEY, filters],
    queryFn: () => fetchSites(filters),
    ...(options.refetchOnMount !== undefined ? { refetchOnMount: options.refetchOnMount } : {}),
  });
}

/** Invalida todas las listas de sedes y el `GET /me` (el `site_scope` puede cambiar de estado). */
function useInvalidateSites() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: SITES_QUERY_KEY });
    await queryClient.invalidateQueries({ queryKey: ['me'] });
  };
}

export function useCreateSite() {
  const invalidate = useInvalidateSites();
  return useMutation<Site, ApiError, CreateSiteBody>({
    mutationFn: (body) => api.post('/sites', body, SiteSchema),
    onSuccess: invalidate,
  });
}

export function useUpdateSite() {
  const invalidate = useInvalidateSites();
  return useMutation<Site, ApiError, { id: string; body: UpdateSiteBody }>({
    mutationFn: ({ id, body }) => api.patch(`/sites/${id}`, body, SiteSchema),
    onSuccess: invalidate,
  });
}

export function useDeactivateSite() {
  const invalidate = useInvalidateSites();
  return useMutation<Site, ApiError, string>({
    mutationFn: (id) => api.post(`/sites/${id}/deactivate`, undefined, SiteSchema),
    onSuccess: invalidate,
  });
}

export function useActivateSite() {
  const invalidate = useInvalidateSites();
  return useMutation<Site, ApiError, string>({
    mutationFn: (id) => api.post(`/sites/${id}/activate`, undefined, SiteSchema),
    onSuccess: invalidate,
  });
}

/**
 * `GET /sites/{id}` (T086 · US7): el detalle de una sede. Para un `SITE_MANAGER` una sede fuera
 * de su alcance responde `404 NOT_FOUND`, indistinguible de un id inventado (FR-011); la pantalla
 * lo traduce en "No encontramos este recurso".
 */
export function useSite(id: string): UseQueryResult<Site, ApiError> {
  return useQuery<Site, ApiError>({
    queryKey: [...SITES_QUERY_KEY, 'detail', id],
    queryFn: () => api.get(`/sites/${id}`, SiteSchema),
  });
}
